<div class="bai-quality-root" aria-label="Quality tab">

  <!-- =====================================================================
       LOADING
  ====================================================================== -->
  @if (loading()) {
    <div class="bai-quality-skeleton" aria-busy="true" aria-label="Loading quality data">
      <p-skeleton width="200px" height="28px" />
      <p-skeleton width="100%" height="120px" styleClass="mt-3" />
      <p-skeleton width="100%" height="200px" styleClass="mt-3" />
    </div>
  }

  <!-- =====================================================================
       NO SESSION
  ====================================================================== -->
  @else if (!state()) {
    <div class="bai-quality-empty" role="status" aria-label="No client selected">
      <i class="pi pi-star bai-quality-empty__icon" aria-hidden="true"></i>
      <p class="bai-quality-empty__title">No client selected</p>
      <p class="bai-quality-empty__sub">Select a client to view their quality data.</p>
    </div>
  }

  <!-- =====================================================================
       MAIN CONTENT
  ====================================================================== -->
  @else {
    @let s = state()!;

    <!-- ----------------------------------------------------------------
         Header + Filters
    ----------------------------------------------------------------- -->
    <div class="bai-quality-header">
      <div class="bai-quality-header__left">
        <h2 class="bai-quality-title">Quality</h2>
        <p class="bai-quality-subtitle">
          Service quality for active and closed cases
        </p>
      </div>
      <div class="bai-quality-header__right">
        <!-- Time window selector -->
        <select
          class="bai-quality-select"
          [value]="s.activeTimeWindow"
          (change)="setTimeWindow($any($event.target).value)"
          aria-label="Select time window"
        >
          @for (opt of timeWindowOptions; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>
        <!-- Count chips -->
        <span class="bai-quality-count-chip" [attr.aria-label]="s.npsEvents.length + ' NPS responses'">
          <i class="pi pi-star-fill" aria-hidden="true"></i>
          {{ s.npsEvents.length }} NPS
        </span>
        <span class="bai-quality-count-chip" [attr.aria-label]="s.earnEvents.length + ' EARN evaluations'">
          <i class="pi pi-chart-bar" aria-hidden="true"></i>
          {{ s.earnEvents.length }} EARN
        </span>
      </div>
    </div>

    <!-- ----------------------------------------------------------------
         Two-column panels: NPS | EARN
    ----------------------------------------------------------------- -->
    <div class="bai-quality-panels">

      <!-- ============================================================
           NPS PANEL
      ============================================================= -->
      <section class="bai-quality-card bai-quality-card--nps" aria-labelledby="nps-panel-title">
        <div class="bai-quality-card__header">
          <h3 id="nps-panel-title" class="bai-quality-card__title">
            <i class="pi pi-star-fill" aria-hidden="true"></i>
            NPS
          </h3>
          <span class="bai-quality-card__badge bai-quality-card__badge--nps">Net Promoter Score</span>
        </div>

        @if (s.npsEvents.length === 0) {
          <div class="bai-quality-empty--inline" role="status">
            <i class="pi pi-inbox" aria-hidden="true"></i>
            <span>No NPS surveys in this window</span>
          </div>
        } @else {
          <!-- Latest NPS score -->
          @if (s.latestNps) {
            @let ln = s.latestNps;
            <div class="bai-quality-nps-latest">
              <div
                class="bai-quality__nps-score"
                [class]="npsScoreClass(ln.category)"
                [attr.aria-label]="'Latest NPS score: ' + ln.score + ' out of 10'"
              >
                {{ ln.score }}<span class="bai-quality__nps-score-denom">/10</span>
              </div>
              <div class="bai-quality-nps-latest__meta">
                <p-tag
                  [value]="npsCategoryLabel(ln.category)"
                  [severity]="npsCategorySeverity(ln.category)"
                  styleClass="text-xs"
                />
                <span class="bai-quality-nps-latest__channel">
                  <i [class]="npsChannelIcon(ln.channel)" aria-hidden="true"></i>
                  {{ npsChannelLabel(ln.channel) }}
                </span>
                <span class="bai-quality-nps-latest__date text-xs text-gray-400">
                  {{ formatTimestamp(ln.timestamp) }}
                </span>
              </div>
            </div>
            <!-- AI summary bullets -->
            @if (ln.aiSummaryBullets.length > 0) {
              <ul class="bai-quality-bullets" aria-label="AI summary for latest NPS">
                @for (bullet of ln.aiSummaryBullets; track $index) {
                  <li>{{ bullet }}</li>
                }
              </ul>
            }
          }

          <!-- NPS timeline entries -->
          <div class="bai-quality-nps-timeline" aria-label="NPS history">
            <h4 class="bai-quality-section-label">Survey History</h4>
            @for (nps of s.npsEvents; track trackById($index, nps)) {
              <div class="bai-quality-nps-row">
                <div class="bai-quality-nps-row__score-wrap">
                  <span
                    class="bai-quality-nps-row__score"
                    [class]="npsScoreClass(nps.category)"
                  >{{ nps.score }}</span>
                  <p-tag
                    [value]="npsCategoryLabel(nps.category)"
                    [severity]="npsCategorySeverity(nps.category)"
                    styleClass="text-xs"
                  />
                </div>
                <div class="bai-quality-nps-row__meta">
                  <span class="text-xs text-gray-500">{{ formatTimestamp(nps.timestamp) }}</span>
                  <span class="bai-quality-nps-row__channel text-xs">
                    <i [class]="npsChannelIcon(nps.channel)" aria-hidden="true"></i>
                    {{ npsChannelLabel(nps.channel) }}
                  </span>
                  <span class="bai-quality-case-chip" [class.bai-quality-case-chip--closed]="nps.caseStatus === 'closed'">
                    {{ nps.caseStatus }}
                  </span>
                  <details class="bai-quality-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                    <summary class="bai-quality-kebab__btn" aria-label="NPS event options">
                      <i class="pi pi-bars" aria-hidden="true"></i>
                    </summary>
                    <button
                      type="button"
                      class="bai-quality-kebab__action"
                      (click)="addNpsEventToCopilot(nps)"
                    >
                      Add to Copilot
                    </button>
                  </details>
                </div>
                <!-- Verbatim toggle -->
                @if (nps.verbatim) {
                  <button
                    type="button"
                    class="bai-quality-verbatim-toggle"
                    (click)="toggleVerbatim(nps.id)"
                    [attr.aria-expanded]="isVerbatimExpanded(nps.id)"
                    [attr.aria-controls]="'verbatim-' + nps.id"
                  >
                    <i [class]="isVerbatimExpanded(nps.id) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" aria-hidden="true"></i>
                    {{ isVerbatimExpanded(nps.id) ? 'Hide' : 'Show' }} feedback
                  </button>
                  @if (isVerbatimExpanded(nps.id)) {
                    <blockquote
                      [id]="'verbatim-' + nps.id"
                      class="bai-quality-verbatim"
                    >"{{ nps.verbatim }}"</blockquote>
                  }
                }
              </div>
            }
          </div>
        }
      </section>

      <!-- ============================================================
           EARN PANEL
      ============================================================= -->
      <section class="bai-quality-card bai-quality-card--earn" aria-labelledby="earn-panel-title">
        <div class="bai-quality-card__header">
          <h3 id="earn-panel-title" class="bai-quality-card__title">
            <i class="pi pi-chart-bar" aria-hidden="true"></i>
            EARN
          </h3>
          <span class="bai-quality-card__badge bai-quality-card__badge--earn">Service Quality Score</span>
        </div>

        @if (s.earnEvents.length === 0) {
          <div class="bai-quality-empty--inline" role="status">
            <i class="pi pi-inbox" aria-hidden="true"></i>
            <span>No EARN evaluations in this window</span>
          </div>
        } @else {
          <!-- Latest EARN score -->
          @if (s.latestEarn) {
            @let le = s.latestEarn;
            <div class="bai-quality-earn-latest">
              <div
                class="bai-quality__earn-score"
                [class]="earnScoreClass(le.score0to100)"
                [attr.aria-label]="'Latest EARN score: ' + le.score0to100 + ' out of 100'"
              >
                {{ le.score0to100 }}<span class="bai-quality__earn-score-denom">/100</span>
              </div>
              <div class="bai-quality-earn-latest__meta">
                <p-tag
                  [value]="earnScoreLabel(le.score0to100)"
                  [severity]="earnScoreSeverity(le.score0to100)"
                  styleClass="text-xs"
                />
                <span class="bai-quality-entity-chip">
                  <i [class]="le.assessedEntityType === 'ai' ? 'pi pi-microchip-ai' : 'pi pi-user'" aria-hidden="true"></i>
                  {{ le.assessedEntityName }}
                </span>
                <span class="text-xs text-gray-400">{{ formatTimestamp(le.timestamp) }}</span>
              </div>
            </div>

            <!-- EARN Dimensions -->
            <div class="bai-quality-dimensions" aria-label="EARN dimensions">
              @for (dim of le.dimensions; track trackByDimId($index, dim)) {
                <div class="bai-quality-dim-card">
                  <div class="bai-quality-dim-card__header">
                    <span class="bai-quality-dim-card__label">{{ dim.label }}</span>
                    <span
                      class="bai-quality-dim-score"
                      [class]="dimScoreClass(dim.score)"
                      [attr.aria-label]="dim.label + ' score: ' + dim.score"
                    >{{ dim.score }}</span>
                  </div>
                  <ul class="bai-quality-dim-attrs" [attr.aria-label]="dim.label + ' attributes'">
                    @for (attr of dim.attributes; track attr.id) {
                      <li
                        class="bai-quality-attr"
                        [class]="attrStatusClass(attr.status)"
                        [pTooltip]="attr.status | titlecase"
                        tooltipPosition="top"
                      >
                        <i [class]="attrStatusIcon(attr.status)" aria-hidden="true"></i>
                        <span>{{ attr.label }}</span>
                      </li>
                    }
                  </ul>
                </div>
              }
            </div>

            <!-- AI summary bullets for latest EARN -->
            @if (le.aiSummaryBullets.length > 0) {
              <ul class="bai-quality-bullets" aria-label="AI summary for latest EARN">
                @for (bullet of le.aiSummaryBullets; track $index) {
                  <li>{{ bullet }}</li>
                }
              </ul>
            }
          }
        }
      </section>

    </div><!-- /.bai-quality-panels -->

    <!-- ----------------------------------------------------------------
         Read-Only Recommendations
    ----------------------------------------------------------------- -->
    @if (s.latestEarn && s.latestEarn.recommendations.length > 0) {
      <section class="bai-quality-card bai-quality-recommendations" aria-labelledby="recs-title">
        <div class="bai-quality-card__header">
          <h3 id="recs-title" class="bai-quality-card__title">
            <i class="pi pi-lightbulb" aria-hidden="true"></i>
            Recommended Improvements
          </h3>
          <span class="bai-quality-readonly-badge" aria-label="Read only">Read-only</span>
        </div>
        <ul class="bai-quality-recs-list" aria-label="Recommended improvements">
          @for (rec of s.latestEarn.recommendations; track $index) {
            <li class="bai-quality-recs-list__item">
              <i class="pi pi-arrow-right" aria-hidden="true"></i>
              {{ rec }}
            </li>
          }
        </ul>
      </section>
    }

    <!-- ----------------------------------------------------------------
         Unified Quality Timeline
    ----------------------------------------------------------------- -->
    <section class="bai-quality-card bai-quality-timeline" aria-labelledby="timeline-title">
      <div class="bai-quality-card__header">
        <h3 id="timeline-title" class="bai-quality-card__title">
          <i class="pi pi-history" aria-hidden="true"></i>
          Quality Timeline
        </h3>
        <span class="text-xs text-gray-400">Reverse chronological · active + closed cases</span>
      </div>

      @if (s.timeline.length === 0) {
        <div class="bai-quality-empty--inline" role="status">
          <i class="pi pi-inbox" aria-hidden="true"></i>
          <span>No quality events in this window</span>
        </div>
      } @else {
        <ol class="bai-quality-timeline-list" aria-label="Quality events timeline">
          @for (event of s.timeline; track trackById($index, event)) {
            <li class="bai-quality-tl-row" [class.bai-quality-tl-row--nps]="event.type === 'nps'" [class.bai-quality-tl-row--earn]="event.type === 'earn'">

              <!-- Type badge -->
              <div class="bai-quality-tl-row__type">
                @if (event.type === 'nps') {
                  <span class="bai-quality-tl-type-chip bai-quality-tl-type-chip--nps">NPS</span>
                } @else {
                  <span class="bai-quality-tl-type-chip bai-quality-tl-type-chip--earn">EARN</span>
                }
              </div>

              <!-- Main content -->
              <div class="bai-quality-tl-row__content">
                <div class="bai-quality-tl-row__meta">
                  <span class="text-xs text-gray-500">{{ formatTimestamp(event.timestamp) }}</span>
                  <span class="bai-quality-case-chip" [class.bai-quality-case-chip--closed]="event.caseStatus === 'closed'">
                    {{ event.caseStatus }}
                  </span>
                  <span class="bai-quality-source-chip">
                    <i [class]="event.source === 'ai' ? 'pi pi-microchip-ai' : event.source === 'fa' ? 'pi pi-user' : 'pi pi-cog'" aria-hidden="true"></i>
                    {{ sourceLabel(event.source) }}
                  </span>
                  <details class="bai-quality-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                    <summary class="bai-quality-kebab__btn" aria-label="Quality timeline event options">
                      <i class="pi pi-bars" aria-hidden="true"></i>
                    </summary>
                    <button
                      type="button"
                      class="bai-quality-kebab__action"
                      (click)="addTimelineEventToCopilot(event)"
                    >
                      Add to Copilot
                    </button>
                  </details>
                </div>

                <!-- NPS-specific -->
                @if (isNpsEvent(event)) {
                  <div class="bai-quality-tl-row__score-row">
                    <span class="bai-quality-tl-nps-score" [class]="npsScoreClass(event.category)">{{ event.score }}/10</span>
                    <p-tag [value]="npsCategoryLabel(event.category)" [severity]="npsCategorySeverity(event.category)" styleClass="text-xs" />
                    <span class="text-xs text-gray-400">
                      <i [class]="npsChannelIcon(event.channel)" aria-hidden="true"></i>
                      {{ npsChannelLabel(event.channel) }}
                    </span>
                  </div>
                }

                <!-- EARN-specific -->
                @if (isEarnEvent(event)) {
                  <div class="bai-quality-tl-row__score-row">
                    <span class="bai-quality-tl-earn-score" [class]="earnScoreClass(event.score0to100)">{{ event.score0to100 }}/100</span>
                    <p-tag [value]="earnScoreLabel(event.score0to100)" [severity]="earnScoreSeverity(event.score0to100)" styleClass="text-xs" />
                    <span class="bai-quality-entity-chip text-xs">
                      <i [class]="event.assessedEntityType === 'ai' ? 'pi pi-microchip-ai' : 'pi pi-user'" aria-hidden="true"></i>
                      {{ event.assessedEntityName }}
                    </span>
                  </div>
                }

                <!-- AI summary bullets -->
                @if (event.aiSummaryBullets.length > 0) {
                  <ul class="bai-quality-bullets bai-quality-bullets--compact" aria-label="AI summary">
                    @for (bullet of event.aiSummaryBullets; track $index) {
                      <li>{{ bullet }}</li>
                    }
                  </ul>
                }

                <!-- Verbatim toggle -->
                @if (event.verbatim) {
                  <button
                    type="button"
                    class="bai-quality-verbatim-toggle"
                    (click)="toggleVerbatim(event.id)"
                    [attr.aria-expanded]="isVerbatimExpanded(event.id)"
                    [attr.aria-controls]="'tl-verbatim-' + event.id"
                  >
                    <i [class]="isVerbatimExpanded(event.id) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" aria-hidden="true"></i>
                    {{ isVerbatimExpanded(event.id) ? 'Hide' : 'Show' }} verbatim
                  </button>
                  @if (isVerbatimExpanded(event.id)) {
                    <blockquote
                      [id]="'tl-verbatim-' + event.id"
                      class="bai-quality-verbatim"
                    >"{{ event.verbatim }}"</blockquote>
                  }
                }
              </div>

            </li>
          }
        </ol>
      }
    </section>

  }<!-- /else main content -->

</div>
